/**
 * tabSession — per-tab (window-scoped) identity for side-by-side demo tools.
 *
 * The whole platform normally authenticates with ONE shared HttpOnly
 * `session_token` cookie, so every tab of the browser collapses onto a
 * single identity. That makes it impossible to compare several demo tools
 * (Corporate / Valet / Cleaners …) at the same time — impersonating in one
 * window rewrites the cookie for all of them.
 *
 * A demo window instead carries its OWN session token in `sessionStorage`
 * (which is scoped to that single tab/window). When such a token is present
 * we transparently:
 *   • attach it as `Authorization: Bearer <token>` on every backend request, and
 *   • send `credentials: 'omit'` so the shared cookie is NOT sent and can
 *     therefore never override the per-tab bearer identity on the server.
 *
 * The admin's own window has no tab token, so it keeps using the cookie and
 * the existing multi-window reconciliation exactly as before.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TAB_KEY = 'necs-tab-session';
const PARAM = 'necs_demo_session';

export function getTabToken() {
  try {
    return sessionStorage.getItem(TAB_KEY) || '';
  } catch {
    return '';
  }
}

export function hasTabSession() {
  return !!getTabToken();
}

export function setTabToken(token) {
  try {
    if (token) sessionStorage.setItem(TAB_KEY, token);
  } catch { /* sessionStorage unavailable — nothing else to do */ }
}

export function clearTabToken() {
  try {
    sessionStorage.removeItem(TAB_KEY);
  } catch { /* noop */ }
}

// Capture a token handed to a freshly opened demo window via the URL, store
// it tab-scoped, then strip it from the address bar so it never lingers in
// history or shows to the user.
function captureFromUrl() {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get(PARAM);
    if (token) {
      setTabToken(token);
      url.searchParams.delete(PARAM);
      window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    }
  } catch { /* noop */ }
}

function isBackendRequest(input) {
  try {
    const url = typeof input === 'string' ? input : input?.url || '';
    if (!url) return false;
    if (BACKEND_URL && url.startsWith(BACKEND_URL)) return true;
    // Relative "/api/..." calls also target the backend through the proxy.
    return url.startsWith('/api/');
  } catch {
    return false;
  }
}

// Monkey-patch window.fetch ONCE. When this tab holds a demo token, inject
// the bearer header and omit cookies for backend calls. Idempotent.
function installFetchInterceptor() {
  if (window.__necsTabFetchPatched) return;
  window.__necsTabFetchPatched = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const token = getTabToken();
    if (!token || !isBackendRequest(input)) {
      return nativeFetch(input, init);
    }
    const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined) || {});
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    return nativeFetch(input, { ...init, headers, credentials: 'omit' });
  };
}

// Run at module import (before the app mounts / before AuthContext runs).
export function initTabSession() {
  if (typeof window === 'undefined') return;
  captureFromUrl();
  installFetchInterceptor();
}

initTabSession();
